import { QuestionCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Radio, Tooltip } from 'antd';
import {
    allowedExtensions,
    FileUploader,
} from 'components/modules/FileUploader';
import { getDefaultLayout, querySerialize } from 'constants/GlobalConfig';
import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { TabKeyEnum } from '..';
import { BACKGROUND_JOB_TYPE } from 'components/modules/Job/Background/BackgroundJobProvider';

const layout = getDefaultLayout(true);

type Props = {
    tabActiveKey: string;
    hasTouchScreen: boolean;
};

export const Domain: React.FC<Props> = ({ hasTouchScreen, tabActiveKey }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const jobInfoMap = useAppSelector((state) => state.jobInfoMap);
    const userChangeDomainCsvImportConnection = useAppSelector(
        (state) => state.userChangeDomainCsvImportConnection
    );

    useEffect(() => {
        if (tabActiveKey !== TabKeyEnum.DOMAIN) return;
        form.resetFields();
        // tabActiveKeyのみ監視したいため
    }, [tabActiveKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const disabledCsvImport = useMemo(() => {
        return (
            userChangeDomainCsvImportConnection.meta.fetch ||
            Object.values(jobInfoMap).filter(
                (jobInfo) =>
                    jobInfo?.job?.type ===
                    BACKGROUND_JOB_TYPE.USER_CHANGE_DOMAIN_CSV_IMPORT
            ).length > 0
        );
    }, [userChangeDomainCsvImportConnection, jobInfoMap]);

    const loadingCsvImport = useMemo(() => {
        return userChangeDomainCsvImportConnection.meta.fetch;
    }, [userChangeDomainCsvImportConnection]);

    const onFinish = (values: Record<string, any>) => {
        const file = values.files[0];
        const fileReader = new FileReader();
        const query = querySerialize({
            encoding: 'utf8',
        });

        fileReader.onload = (event) => {
            dispatch(
                Actions.http.connection.users.importCsvChangeDomain(
                    query,
                    file.type,
                    event?.target?.result
                )
            );
        };
        fileReader.readAsArrayBuffer(file);
    };

    const submit = () => {
        form.submit();
    };

    return (
        <div className='container'>
            <div
                className='card'
                style={{ marginBottom: '32px', padding: '32px' }}
            >
                <p
                    className='gutter-bottom text-lg'
                    style={{ fontWeight: 'bold' }}
                >
                    ドメインの一括変更
                </p>
                <p>
                    CSVファイルで指定したユーザーのドメインを変更します。
                    <br />
                    処理件数により時間がかかることがあります。
                </p>
                <Alert
                    message={<DefaultAlertMessage />}
                    type='warning'
                    style={{ marginBottom: '1rem' }}
                    showIcon
                />
                <p>
                    数字のゼロやハイフンなどの記号の欠落が無いことを確認してください。
                </p>

                <Form
                    {...layout}
                    form={form}
                    onFinish={onFinish}
                >
                    <Form.Item
                        name='files'
                        label='ユーザー情報を含むCSVファイル'
                        rules={[
                            {
                                required: true,
                                message: 'ファイルを添付してください',
                            },
                        ]}
                    >
                        <FileUploader
                            allowedExtension={allowedExtensions.csv}
                            useRawData={true}
                        />
                    </Form.Item>
                </Form>

                <div className='flex-right-container'>
                    <Button
                        className='mb-4'
                        type='primary'
                        onClick={submit}
                        disabled={disabledCsvImport}
                        loading={loadingCsvImport}
                    >
                        インポートを実行
                    </Button>
                </div>
            </div>
        </div>
    );
};

const DefaultAlertMessage: React.FC = () => {
    return (
        <span>
            指定した新旧ユーザーIDでユーザー名 (@より前の部分)
            が一致しているユーザーのみ、登録しているドメインが変更されます。
            <br />
            CSVファイルの１行目は無視されます。
            <br />
            進捗は画面左下よりご確認いただけます。
            <br />
            同一ブラウザであれば、ブラウザを閉じても前回の結果を確認することが可能です。
            <br />
        </span>
    );
};
